/** @jsxImportSource preact */
import { useEffect, useRef, useState } from "preact/hooks";
import type { Address } from "../../shared/types";
import { api } from "../api";
import { Avatar } from "./Avatar";

const ADDRESS = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

/** "Name <a@b>" or a bare address; anything else stays a chip marked invalid. */
const parseOne = (value: string): Address | null => {
  const trimmed = value.trim().replace(/[,;]+$/, "");
  const angled = /^(.*?)\s*<([^<>]+)>$/.exec(trimmed);
  if (angled) {
    const address = (angled[2] ?? "").trim();
    return ADDRESS.test(address)
      ? { name: (angled[1] ?? "").trim().replace(/^"(.*)"$/, "$1"), address }
      : null;
  }
  return ADDRESS.test(trimmed) ? { name: "", address: trimmed } : null;
};

export const formatChip = (address: Address) =>
  address.name ? `${address.name} <${address.address}>` : address.address;

export const RecipientInput = ({
  label,
  value,
  onChange,
  autofocus,
}: {
  label: string;
  value: Address[];
  onChange: (value: Address[]) => void;
  autofocus?: boolean;
}) => {
  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState<Address[]>([]);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autofocus) input.current?.focus();
  }, []);

  useEffect(() => {
    const query = text.trim();
    if (query.length < 1) {
      setSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .get<Address[]>(`/contacts/suggest?q=${encodeURIComponent(query)}`, controller.signal)
        .then((list) => {
          const taken = new Set(value.map((entry) => entry.address.toLowerCase()));
          setSuggestions(list.filter((entry) => !taken.has(entry.address.toLowerCase())));
          setActive(0);
        })
        .catch(() => {});
    }, 120);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [text]);

  const add = (entries: Address[]) => {
    const seen = new Set(value.map((entry) => entry.address.toLowerCase()));
    const next = [...value];
    for (const entry of entries) {
      if (seen.has(entry.address.toLowerCase())) continue;
      seen.add(entry.address.toLowerCase());
      next.push(entry);
    }
    onChange(next);
    setText("");
    setSuggestions([]);
  };

  /** Turns typed or pasted text into chips; returns false if some of it is not an address. */
  const commit = (raw: string): boolean => {
    const parts = raw
      .split(/[,;\n]+(?=(?:[^"]*"[^"]*")*[^"]*$)/)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) return true;
    const parsed = parts.map(parseOne);
    const good = parsed.filter((entry): entry is Address => entry !== null);
    add(good);
    const bad = parts.filter((_, index) => parsed[index] === null);
    if (bad.length > 0) setText(bad.join(", "));
    return bad.length === 0;
  };

  return (
    <div class="recipient-field" onClick={() => input.current?.focus()}>
      <span class="recipient-label">{label}</span>
      <div class="chips">
        {value.map((entry, index) => (
          <span class="chip" key={entry.address} title={entry.address}>
            <Avatar person={entry} size="tiny" />
            <span class="chip-label">{entry.name || entry.address}</span>
            <button
              type="button"
              aria-label={`Remove ${entry.address}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(value.filter((_, position) => position !== index));
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={input}
          aria-label={label}
          value={text}
          autocomplete="off"
          onInput={(event) => {
            const next = (event.target as HTMLInputElement).value;
            if (/[,;]\s*$/.test(next)) commit(next);
            else setText(next);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" && suggestions.length > 0) {
              event.preventDefault();
              setActive((active + 1) % suggestions.length);
            } else if (event.key === "ArrowUp" && suggestions.length > 0) {
              event.preventDefault();
              setActive((active - 1 + suggestions.length) % suggestions.length);
            } else if (
              (event.key === "Enter" || event.key === "Tab") &&
              suggestions[active] &&
              text.trim()
            ) {
              event.preventDefault();
              add([suggestions[active] as Address]);
            } else if (event.key === "Enter" && text.trim()) {
              event.preventDefault();
              commit(text);
            } else if (event.key === "Backspace" && text === "" && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onPaste={(event) => {
            const pasted = event.clipboardData?.getData("text") ?? "";
            if (/[,;\n]/.test(pasted)) {
              event.preventDefault();
              commit(`${text}${pasted}`);
            }
          }}
          onBlur={() => {
            setTimeout(() => setSuggestions([]), 150);
            if (text.trim()) commit(text);
          }}
        />
      </div>
      {suggestions.length > 0 && (
        <div class="suggestions" role="listbox">
          {suggestions.map((entry, index) => (
            <button
              type="button"
              role="option"
              aria-selected={index === active}
              class={index === active ? "active" : ""}
              key={entry.address}
              onMouseDown={(event) => {
                event.preventDefault();
                add([entry]);
              }}
            >
              <Avatar person={entry} size="tiny" />
              <span>
                {entry.name && <strong>{entry.name} </strong>}
                <span class="muted">{entry.address}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
